// src/pages/admin/invoices/InvoicesList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Input,
  Button,
  Alert,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
  Select,
  Option,
} from "@material-tailwind/react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../../firebaseConfig";
import { collection, getDocs, deleteDoc, doc, getDoc } from "firebase/firestore";
import {
  MagnifyingGlassIcon,
  PrinterIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import ReactToPrint from "react-to-print";
import { useNavigate, useSearchParams } from "react-router-dom";
import { printInvoiceToTwoPrinters } from "../../../printing/printService";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

const ui = {
  pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
  headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
  cardBorder: `1px solid ${BRAND.paper}`,
  cardShadow: "0 18px 50px rgba(0,0,0,0.08), 0 6px 18px rgba(0,0,0,0.05)",
  softShadow: "0 10px 26px rgba(0,0,0,0.06)",
  ring: "0 0 0 4px rgba(135,135,135,0.16)",
  tableHeadBg: BRAND.paper,
  rowHover: "rgba(239,239,239,0.65)",
};

export default function InvoicesList() {
  const [me, setMe] = useState(null);
  const [role, setRole] = useState(Number(localStorage.getItem("role") || "2"));
  const isAdmin = role === 1;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [view, setView] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setMe(u || null);
      if (u?.uid) {
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          const r =
            snap.exists() && snap.data().role !== undefined
              ? Number(snap.data().role)
              : Number(localStorage.getItem("role") || "2");
          setRole(r);
          localStorage.setItem("role", String(r));
        } catch {}
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      if (!me?.uid) return;
      if (isAdmin) {
        try {
          const snap = await getDocs(collection(db, "users"));
          const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          list.sort((a, b) =>
            String(a.storeName || a.displayName || a.name || a.email || "").localeCompare(
              String(b.storeName || b.displayName || b.name || b.email || ""),
              "ar"
            )
          );
          setUsers(list);
          const urlUid = searchParams.get("uid");
          setSelectedUid(urlUid || list[0]?.id || me.uid);
        } catch {}
      } else {
        setSelectedUid(me.uid);
      }
    })();
    // eslint-disable-next-line
  }, [me, isAdmin]);

  async function load() {
    if (!selectedUid) return;
    setErr("");
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users", selectedUid, "invoices"));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setRows(list);
    } catch (e) {
      console.error(e);
      setErr("تعذر جلب الفواتير.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [selectedUid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = dateFrom ? new Date(dateFrom).getTime() : 0;
    const toTs = dateTo
      ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).getTime()
      : Number.MAX_SAFE_INTEGER;

    return rows.filter((r) => {
      const t = r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0;
      const inRange = t >= fromTs && t <= toTs;
      const sOk = q
        ? String(r.customerName || "").toLowerCase().includes(q) ||
          String(r.customerPhone || "").toLowerCase().includes(q) ||
          String(r.id || "").toLowerCase().includes(q) ||
          String(r.invoiceNo || "").includes(q)
        : true;
      return inRange && sOk;
    });
  }, [rows, search, dateFrom, dateTo]);

  function openView(row) {
    setView(row);
  }

  function formatDate(ts) {
    const d = ts?.seconds ? new Date(ts.seconds * 1000) : new Date();
    return d.toLocaleString("ar-IQ");
  }

  const totalItems = (r) =>
    Array.isArray(r.items) ? r.items.reduce((s, i) => s + (i.qty || 0), 0) : 0;

  function goBack() {
    if (window.history.length > 1) nav(-1);
    else nav("/");
  }

  function askDelete(row) {
    if (role !== 1) {
      setErr("ليس لديك صلاحية الحذف (للمشرف فقط).");
      return;
    }
    setToDelete(row);
    setConfirmOpen(true);
  }

  function closeConfirm() {
    setConfirmOpen(false);
    setToDelete(null);
  }

  async function confirmDelete() {
    if (role !== 1) {
      setErr("ليس لديك صلاحية الحذف (للمشرف فقط).");
      return;
    }
    if (!selectedUid || !toDelete?.id) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "users", selectedUid, "invoices", toDelete.id));
      setRows((prev) => prev.filter((r) => r.id !== toDelete.id));
      if (view?.id === toDelete.id) setView(null);
      closeConfirm();
    } catch (e) {
      console.error(e);
      setErr("تعذر حذف الفاتورة.");
    } finally {
      setDeleting(false);
    }
  }

  async function directPrint(row) {
    try {
      const items = Array.isArray(row.items) ? row.items : [];
      const subtotal = items.reduce(
        (s, i) => s + Number(i.price || 0) * Number(i.qty || 0),
        0
      );
      const discount = Number(row.discount || 0);
      const total = Number(
        row.total || Math.max(0, subtotal - (subtotal * discount) / 100)
      );

      await printInvoiceToTwoPrinters({
        invoiceNo: row.invoiceNo,
        lines: items,
        subtotal,
        discount,
        total,
        customerName: row.customerName || "",
        brand: BRAND, // ✅ نفس ألوان الصورة فقط
      });
    } catch (e) {
      console.error(e);
      alert("فشل الطباعة المباشرة");
    }
  }

  const currentUserName = useMemo(() => {
    if (!isAdmin) return "";
    const u = users.find((x) => x.id === selectedUid);
    return u ? u.storeName || u.displayName || u.name || u.email || u.id : "";
  }, [users, selectedUid, isAdmin]);

  return (
    <div
      dir="rtl"
      className="p-4 min-h-screen font-tajawal"
      style={{ background: ui.pageBg }}
    >
      <Card
        className="mx-auto max-w-7xl bg-white rounded-3xl overflow-hidden"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div
            className="flex items-center justify-between px-6 py-6"
            style={{ background: ui.headerBg }}
          >
            <button
              onClick={goBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography
                variant="h4"
                className="text-white font-extrabold"
                style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
              >
                الفواتير {isAdmin && currentUserName ? `– ${currentUserName}` : ""}
              </Typography>
              <div
                className="mt-2 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: "Tajawal, system-ui, sans-serif",
                  fontWeight: 800,
                }}
              >
                <span>عدد النتائج:</span>
                <span>{filtered.length}</span>
              </div>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-4 p-6">
          {err && (
            <Alert
              color="red"
              className="font-tajawal"
              style={{ borderRadius: 16 }}
            >
              {err}
            </Alert>
          )}

          {/* شريط الأدوات */}
          <div
            className="rounded-2xl p-4"
            style={{
              background: BRAND.white,
              border: `1px solid ${BRAND.paper}`,
              boxShadow: ui.softShadow,
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {isAdmin && (
                <div className="flex-1 md:col-span-2">
                  <Select
                    label="اختر المستخدم"
                    value={selectedUid}
                    onChange={(v) => setSelectedUid(v || "")}
                    className="font-tajawal"
                    labelProps={{ className: "font-tajawal" }}
                  >
                    {users.map((u) => {
                      const name = u.storeName || u.displayName || u.name || u.email || u.id;
                      return (
                        <Option key={u.id} value={u.id} className="font-tajawal">
                          {name}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              )}

              <div className="flex-1">
                <Typography className="text-xs mb-1 font-tajawal" style={{ color: BRAND.graphite }}>
                  بحث
                </Typography>
                <Input
                  label="بحث بالاسم/الهاتف/رقم"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                  crossOrigin=""
                  className="font-tajawal"
                  labelProps={{ className: "font-tajawal" }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              <div className="flex items-end gap-2 md:col-span-2">
                <div className="flex-1">
                  <Typography className="text-xs mb-1 font-tajawal" style={{ color: BRAND.graphite }}>
                    من
                  </Typography>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    crossOrigin=""
                    className="font-tajawal"
                    labelProps={{ className: "font-tajawal" }}
                  />
                </div>

                <div className="flex-1">
                  <Typography className="text-xs mb-1 font-tajawal" style={{ color: BRAND.graphite }}>
                    إلى
                  </Typography>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    crossOrigin=""
                    className="font-tajawal"
                    labelProps={{ className: "font-tajawal" }}
                  />
                </div>

                <Button
                  variant="outlined"
                  onClick={load}
                  disabled={loading}
                  className="flex items-center gap-2 font-tajawal rounded-2xl"
                  style={{
                    borderColor: BRAND.mist,
                    color: BRAND.ink,
                  }}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  {loading ? "..." : "تحديث"}
                </Button>

                <Button
                  size="sm"
                  variant="text"
                  className="font-tajawal"
                  style={{ color: BRAND.graphite }}
                  onClick={() => nav("/brand?auto=1")}
                >
                  إنشاء الهوية البصرية
                </Button>
              </div>
            </div>
          </div>

          {/* الجدول */}
          <div
            className="overflow-auto rounded-2xl"
            style={{ border: `1px solid ${BRAND.paper}`, boxShadow: ui.softShadow }}
          >
            <table className="w-full text-right">
              <thead>
                <tr style={{ backgroundColor: ui.tableHeadBg }}>
                  {["رقم الفاتورة", "التاريخ", "الزبون", "عدد الأصناف", "الإجمالي", "إجراءات"].map(
                    (h) => (
                      <th
                        key={h}
                        className="p-3 text-sm font-extrabold"
                        style={{
                          color: BRAND.ink,
                          borderBottom: `1px solid ${BRAND.mist}`,
                          fontFamily: "Tajawal, system-ui, sans-serif",
                        }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {filtered.length ? (
                  filtered.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-t transition"
                      style={{
                        borderColor: BRAND.paper,
                        background: idx % 2 === 0 ? BRAND.white : "#FFFFFF",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = ui.rowHover)}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = idx % 2 === 0 ? BRAND.white : "#FFFFFF")
                      }
                    >
                      <td className="p-3">{r.invoiceNo || "-"}</td>
                      <td className="p-3">{formatDate(r.createdAt)}</td>
                      <td className="p-3">{r.customerName || "-"}</td>
                      <td className="p-3">{totalItems(r)}</td>
                      <td className="p-3" style={{ color: BRAND.ink, fontWeight: 800 }}>
                        {Number(r.total || 0).toLocaleString("en-EG")} د.ع
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={() => openView(r)}
                            className="font-tajawal rounded-2xl"
                            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                          >
                            معاينة
                          </Button>

                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={() => directPrint(r)}
                            className="flex items-center gap-2 font-tajawal rounded-2xl"
                            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                          >
                            <PrinterIcon className="h-5 w-5" />
                            طباعة مباشرة
                          </Button>

                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => askDelete(r)}
                              className="flex items-center gap-2 font-tajawal rounded-2xl"
                              style={{ borderColor: BRAND.mist, color: "#7f1d1d" }}
                            >
                              <TrashIcon className="h-5 w-5" />
                              حذف
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="p-6 text-center"
                      colSpan={6}
                      style={{ color: BRAND.graphite }}
                    >
                      لا توجد فواتير.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>

        <CardFooter className="p-4" />
      </Card>

      <InvoiceDialog
        row={view}
        canDelete={isAdmin}
        onClose={() => setView(null)}
        onAskDelete={(r) => askDelete(r)}
        brand={BRAND}
      />

      {/* تأكيد الحذف */}
      <Dialog
        dir="rtl"
        open={confirmOpen}
        handler={closeConfirm}
        size="sm"
        className="font-tajawal"
      >
        <DialogHeader className="justify-between">
          <Typography variant="h6" style={{ color: "#7f1d1d" }}>
            تأكيد الحذف
          </Typography>
          <IconButton variant="text" color="blue-gray" onClick={closeConfirm}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody divider>
          {toDelete ? (
            <div className="space-y-1">
              <p>
                هل أنت متأكد من حذف الفاتورة رقم <b>#{toDelete.invoiceNo || "-"}</b>؟
              </p>
              <p className="text-sm" style={{ color: BRAND.graphite }}>
                هذا الإجراء لا يمكن التراجع عنه.
              </p>
            </div>
          ) : (
            <p>لا توجد فاتورة محددة.</p>
          )}
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <Button
            variant="text"
            onClick={closeConfirm}
            disabled={deleting}
            className="font-tajawal"
            style={{ color: BRAND.graphite }}
          >
            إلغاء
          </Button>

          {isAdmin && (
            <Button
              onClick={confirmDelete}
              className="flex items-center gap-2 font-tajawal rounded-2xl"
              disabled={deleting}
              style={{
                background: `linear-gradient(135deg, #7f1d1d, #991b1b)`,
                color: "white",
              }}
            >
              <TrashIcon className="h-5 w-5" />
              {deleting ? "جارٍ الحذف..." : "حذف الفاتورة"}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function InvoiceDialog({ row, onClose, onAskDelete, canDelete, brand }) {
  const printRef = useRef();
  if (!row) return null;

  const items = Array.isArray(row.items) ? row.items : [];
  const subtotal = items.reduce(
    (s, i) => s + Number(i.price || 0) * Number(i.qty || 0),
    0
  );
  const discount = Number(row.discount || 0);
  const total = Number(row.total || Math.max(0, subtotal - (subtotal * discount) / 100));

  return (
    <Dialog dir="rtl" open={!!row} handler={onClose} size="sm" className="font-tajawal">
      <DialogHeader className="justify-between">
        <Typography variant="h6" style={{ color: brand.ink }}>
          فاتورة #{row.invoiceNo || "-"}
        </Typography>
        <IconButton variant="text" color="blue-gray" onClick={onClose}>
          <XMarkIcon className="h-6 w-6" />
        </IconButton>
      </DialogHeader>

      <DialogBody divider>
        <div className="text-sm" style={{ color: brand.ink }}>
          <div>
            التاريخ:{" "}
            {row.createdAt?.seconds
              ? new Date(row.createdAt.seconds * 1000).toLocaleString("ar-IQ")
              : "-"}
          </div>
          <div>الزبون: {row.customerName || "-"}</div>
          <div>الهاتف: {row.customerPhone || "-"}</div>
          <div className="mt-2" style={{ color: brand.graphite }}>
            ملاحظات: {row.notes || "-"}
          </div>
        </div>

        <div className="mt-3 overflow-auto rounded-xl" style={{ border: `1px solid ${brand.paper}` }}>
          <table className="w-full text-right text-xs">
            <thead style={{ backgroundColor: brand.paper }}>
              <tr>
                <th className="p-2" style={{ color: brand.ink }}>المنتج</th>
                <th className="p-2" style={{ color: brand.ink }}>كمية</th>
                <th className="p-2" style={{ color: brand.ink }}>سعر</th>
                <th className="p-2" style={{ color: brand.ink }}>المجموع</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-t" style={{ borderColor: brand.paper }}>
                  <td className="p-2">{it.name}</td>
                  <td className="p-2">{it.qty}</td>
                  <td className="p-2">{Number(it.price || 0).toLocaleString("en-EG")}</td>
                  <td className="p-2">
                    {Number((it.price || 0) * (it.qty || 0)).toLocaleString("en-EG")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 rounded-xl p-3" style={{ background: brand.white, border: `1px solid ${brand.paper}` }}>
          <div className="flex justify-between text-sm" style={{ color: brand.graphite }}>
            <span>المجموع</span>
            <span>{subtotal.toLocaleString("en-EG")} د.ع</span>
          </div>
          <div className="flex justify-between text-base font-extrabold mt-1" style={{ color: brand.ink }}>
            <span>الإجمالي</span>
            <span>{total.toLocaleString("en-EG")} د.ع</span>
          </div>
        </div>

        <div className="hidden">
          <InvoicePrint ref={printRef} row={row} subtotal={subtotal} total={total} brand={brand} />
        </div>
      </DialogBody>

      <DialogFooter className="justify-end gap-2">
        <ReactToPrint
          trigger={() => (
            <Button
              className="flex items-center gap-2 font-tajawal rounded-2xl"
              style={{ background: `linear-gradient(135deg, ${brand.ink}, ${brand.graphite})`, color: "white" }}
            >
              <PrinterIcon className="h-5 w-5" />
              طباعة
            </Button>
          )}
          content={() => printRef.current}
        />

        {canDelete && (
          <Button
            variant="outlined"
            onClick={() => onAskDelete?.(row)}
            className="flex items-center gap-2 font-tajawal rounded-2xl"
            style={{ borderColor: brand.mist, color: "#7f1d1d" }}
          >
            <TrashIcon className="h-5 w-5" />
            حذف
          </Button>
        )}

        <Button
          variant="text"
          onClick={onClose}
          className="font-tajawal"
          style={{ color: brand.graphite }}
        >
          إغلاق
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

const InvoicePrint = React.forwardRef(function InvoicePrint({ row, subtotal, total, brand }, ref) {
  const items = Array.isArray(row.items) ? row.items : [];
  return (
    <div ref={ref} className="p-6 w-[80mm]" style={{ color: brand.ink, fontFamily: "Tajawal, system-ui, sans-serif" }}>
      <div className="text-center">
        <h1 className="text-lg font-extrabold">فاتورة بيع</h1>
        <div className="text-xs" style={{ color: brand.graphite }}>
          #{row.invoiceNo || "-"} —{" "}
          {row.createdAt?.seconds ? new Date(row.createdAt.seconds * 1000).toLocaleString("ar-IQ") : ""}
        </div>
      </div>

      <div className="mt-2 text-xs">
        <div>الزبون: {row.customerName || "-"}</div>
        <div>الهاتف: {row.customerPhone || "-"}</div>
      </div>

      <table className="w-full mt-3 text-xs">
        <thead>
          <tr style={{ borderBottom: `1px solid ${brand.mist}` }}>
            <th className="py-1 text-right">المنتج</th>
            <th className="py-1 text-right">كمية</th>
            <th className="py-1 text-right">سعر</th>
            <th className="py-1 text-right">المجموع</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${brand.paper}` }}>
              <td className="py-1">{l.name}</td>
              <td className="py-1">{l.qty}</td>
              <td className="py-1">{Number(l.price || 0).toLocaleString("en-EG")}</td>
              <td className="py-1">{Number((l.price || 0) * (l.qty || 0)).toLocaleString("en-EG")}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2 text-xs">
        <div className="flex justify-between">
          <span style={{ color: brand.graphite }}>المجموع</span>
          <span>{subtotal.toLocaleString("en-EG")}</span>
        </div>
        <div className="flex justify-between font-extrabold text-base">
          <span>الإجمالي</span>
          <span>{total.toLocaleString("en-EG")}</span>
        </div>
      </div>

      {row.notes && <div className="mt-2 text-xs" style={{ color: brand.graphite }}>ملاحظات: {row.notes}</div>}

      <div className="mt-3 text-center text-[11px]" style={{ color: brand.graphite }}>
        شكراً لكم!
      </div>
    </div>
  );
});
